import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { updateBulkTransportList } from '~/store/dailyparts/actions';
import { Grid, withStyles } from '@material-ui/core';
import BulkFormModal from '../BulkFormModal';
import PF2MSelectOption from '~/components/PF2MSelectOption';
import PF2MNumericField from '~/components/PF2MNumericField';
import {
  formatData,
  getFieldError,
  isFormValid,
  getSlicedData,
  optionsToSelectBatch,
} from './utils';
import styles from './styles';

const TransportBulkUpdateModal = ({
  open,
  setOpen,
  filters,
  bulkUpdateList,
  originalList,
  pagination,
}) => {
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  const [values, setValues] = useState({});
  const equipmentGroup = useSelector(state => state.manager.equipmentsGroups);
  const equipment = useSelector(state => state.manager.equipments);
  const operatorGroup = useSelector(state => state.manager.operatorsGroups);
  const operator = useSelector(state => state.manager.operators);
  const material = useSelector(state => state.manager.materials);
  const turns = useSelector(state => state.manager.turns);
  const teams = useSelector(state => state.manager.teams);
  const elements = useSelector(state => state.manager.elements);
  const subElements = useSelector(state => state.manager.subElements);

  const {
    equipmentsGroups,
    equipments,
    loadEquipments,
    originElement,
    originPointElement,
    destinationElement,
    destinationPointElement,
    operatorGroups,
    operators,
    materials,
    turn,
    team,
  } = formatData({
    equipmentGroup,
    equipment,
    operatorGroup,
    operator,
    material,
    turns,
    teams,
    elements,
    subElements,
    filters,
    values,
    translate,
  });

  const fieldDependecies = {
    id_operator_truck_group: 'id_operator_truck',
    id_mine: 'id_mine_origin',
    id_destination: 'id_destination_point',
    id_group: 'id_truck',
  };
  const allowZeroFields = [
    'load_manager',
    'load_balance',
    'empty_distance',
    'full_distance',
  ];

  const formFields = [
    {
      key: 'id_group',
      description: translate('common:EquipmentGroup'),
      component: 'select',
      options: equipmentsGroups,
    },
    {
      key: 'id_truck',
      description: translate('common:Equipament'),
      component: 'select',
      options: equipments,
      dependsOn: 'id_group',
    },
    {
      key: 'id_load_equip',
      description: translate('common:LoadEquipment'),
      component: 'select',
      options: loadEquipments,
    },
    {
      key: 'id_mine',
      description: translate('common:Origin'),
      component: 'select',
      options: originElement,
    },
    {
      key: 'id_mine_origin',
      description: translate('common:OriginalPoint'),
      component: 'select',
      options: originPointElement,
      dependsOn: 'id_mine',
    },
    {
      key: 'id_destination',
      description: translate('common:Destination'),
      component: 'select',
      options: destinationElement,
    },
    {
      key: 'id_destination_point',
      description: translate('common:DestinationPoint'),
      component: 'select',
      options: destinationPointElement,
      dependsOn: 'id_destination',
    },
    {
      key: 'load_manager',
      description: translate('common:LoadDefault'),
      component: 'numeric',
    },
    {
      key: 'load_balance',
      description: translate('common:LoadBalance'),
      component: 'numeric',
    },
    {
      key: 'id_operator_truck_group',
      description: translate('common:OperatorGroup'),
      component: 'select',
      options: operatorGroups,
    },
    {
      key: 'id_operator_truck',
      description: translate('common:Operator'),
      component: 'select',
      options: operators,
      dependsOn: 'id_operator_truck_group',
    },
    {
      key: 'id_material_truck',
      description: translate('common:Material'),
      component: 'select',
      options: materials,
    },
    {
      key: 'turn',
      description: translate('common:Shift'),
      component: 'select',
      options: turn,
    },
    {
      key: 'team_id',
      description: translate('common:Team'),
      component: 'select',
      options: team,
    },
  ];

  const data = {
    type: translate('common:Transport'),
  };

  const handleChange = (field, value) => {
    setValues((prev) => {
      const updatedValue = { ...prev };
      const isZeroValue = value === 0;
      const previousValue = prev[field];
      const fieldChanged = previousValue !== value;
      const allowZero = allowZeroFields.includes(field);

      const dependent = fieldDependecies[field];

      if (isZeroValue && !allowZero) {
        delete updatedValue[field];

        if (dependent) {
          delete updatedValue[dependent];
        }
        return updatedValue;
      }

      if (fieldChanged && dependent) {
        delete updatedValue[dependent];
      }
      updatedValue[field] = value;
      return updatedValue;
    });
  };

  const handleClose = () => {
    setOpen(false);
  };
  const handleSave = ({ sliceStart, sliceEnd }) => {
    if (
      !bulkUpdateList
      || !bulkUpdateList.length
      || !isFormValid({ values, fieldDependecies, allowZeroFields })
      || sliceStart === undefined
      || sliceEnd === undefined
    ) return null;

    const updatedData = getSlicedData(sliceStart, sliceEnd, bulkUpdateList);

    const originalDataSliced = getSlicedData(
      sliceStart,
      sliceEnd,
      originalList,
    );

    const updatedIds = new Set(updatedData.map(d => d.id));
    const originalData = originalDataSliced.filter(d => updatedIds.has(d.id));

    const filledValues = Object.entries(values).reduce((acc, [key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});

    const updatedList = updatedData.map(item => ({
      start_date: item.start_date,
      start_time: item.start_time,
      end_date: item.end_date,
      end_time: item.end_time,
      id: item.id,
      team_id: item.team_id,
      ...filledValues,
      isDirty: true,
      update_status: 'U',
    }));

    dispatch(
      updateBulkTransportList({
        data: updatedList,
        ogData: originalData,
        saveLog: true,
        parameters: filters,
      }),
    );
    setOpen(false);
    return updatedList;
  };

  return (
    <BulkFormModal
      open={open}
      onClose={() => handleClose()}
      disableSave={!isFormValid({ values, fieldDependecies, allowZeroFields })}
      data={data}
      onSave={handleSave}
      batchOptions={optionsToSelectBatch({ pagination, list: bulkUpdateList })}
    >
      <Grid container spacing={3} wrap="wrap">
        {formFields.map(field => (
          <Grid item md={3} sm={6} xs={12} key={field.key}>
            {field.component === 'select' ? (
              <PF2MSelectOption
                description={field.description}
                value={values?.[field.key] || 0}
                onChange={e => handleChange(field.key, e.target?.value)}
                options={field.options}
                disabled={field.dependsOn && !values?.[field.dependsOn]}
                error={
                  field.dependsOn
                  && getFieldError({
                    fieldDependecies,
                    fieldKey: field.key,
                    values,
                    allowZeroFields,
                  })
                }
              />
            ) : (
              <PF2MNumericField
                description={field.description}
                value={values?.[field.key]}
                onChange={value => handleChange(field.key, value)}
                placeholder={translate('common:InsertValue')}
              />
            )}
          </Grid>
        ))}
      </Grid>
    </BulkFormModal>
  );
};

TransportBulkUpdateModal.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func,
  filters: PropTypes.object,
  bulkUpdateList: PropTypes.array,
  originalList: PropTypes.array,
  pagination: PropTypes.shape({
    batchSize: PropTypes.number,
  }),
};

TransportBulkUpdateModal.defaultProps = {
  setOpen: () => {},
  filters: {},
  bulkUpdateList: [],
  originalList: [],
  pagination: {},
};

export default withStyles(styles)(TransportBulkUpdateModal);
